import type { TimelineWrite } from "@pax/carto";
import {
  type AdvisorEntry,
  type Alias,
  type CommittedDelta,
  type Extent,
  emptyTimeline,
  type Fork,
  type Input,
  mapAtEnd,
  type Perspective,
  type PerspectiveChangeRequest,
  type PerspectiveSuggestion,
  type PlayerPlace,
  type PollResponse,
  type Properties,
  type PropertiesChanges,
  type ProposingUser,
  reachedViews,
  resolveAlias,
  sceneStory,
  speakerKey,
  type Thing,
  type ThreadEntry,
  type ThreadMember,
  type ThreadSpeaker,
  type ThreadStatus,
  type Timeline,
  targetOf,
  type UserDelta,
  type UserHistory,
  type ViewRef,
  type WorldHistory,
  writeTimeline,
} from "@pax/world";

/** What the spectator and every perspective share: the map and the story so far. */
export interface ViewState {
  /** The writes on the view's timeline, in the order it saw them. */
  writes: readonly TimelineWrite[];
  /**
   * The view's history as its writes wrote it, with its labels. Views that saw the same writes
   * share one, which is never changed in place, and so do their maps.
   */
  timeline: Timeline;
  /** Every thing on the map where the timeline ends, as it is there. */
  map: Thing[];
  /** Every scene the view has been shown, oldest first. */
  scenes: SceneState[];
  /** How the view sees each perspective, by handle. */
  aliases: Record<string, Alias>;
}

/** A scene a view has been shown. */
interface SceneState {
  id: string;
  title: string;
  story: string;
  /** The revision that showed it. */
  revision: number;
}

export interface SpectatorState extends ViewState {
  perspectiveSuggestions: PerspectiveSuggestion[];
}

interface PollState {
  status: "open" | "closed";
  /** One per respondent; a later response replaces an earlier one. */
  responses: { respondent: ThreadSpeaker; response: PollResponse }[];
}

/** One mark on a message, and everyone who left it, in the order they did. */
interface ThreadReaction {
  mark: string;
  reactors: ThreadSpeaker[];
}

export interface ThreadState {
  id: string;
  title: string;
  status: ThreadStatus;
  /** What the conversation is about on the map. */
  frame?: Extent;
  members: ThreadMember[];
  entries: ThreadEntry[];
  polls: Record<string, PollState>;
  /** The marks on each message, by entry id, in the order they were first left. */
  reactions: Record<string, ThreadReaction[]>;
}

export interface ActionState {
  id: string;
  text: string;
  status: "pending" | "committed" | "withdrawn";
  /** The round whose jump sent it, once one has. */
  round?: number;
}

export interface PerspectiveState extends ViewState {
  perspective: Perspective;
  /** The users playing it. While it has any, it receives the world's round deltas. */
  userIds: string[];
  /** Its ask for the next jump. A jump starting drops it. */
  jumpRequest: { guidance?: string } | null;
  /** The input it was last asked, until it is answered or withdrawn. */
  awaits: Input | null;
  threads: ThreadState[];
  /** Threads the players keep at the top of their list, in order. */
  pinnedThreadIds: string[];
  actions: ActionState[];
  /** The players' own place for entries, by board and entry. Entries left out keep the board's. */
  boardPlaces: Record<string, Record<string, PlayerPlace>>;
}

interface UserState {
  /**
   * The user's advisor entries, by the handle of the perspective they were on when each was
   * written. A copy holds only the perspectives it sees, so entries written on others are left out.
   */
  advisor: Record<string, AdvisorEntry[]>;
  /** The user's latest ask to change perspective, until it is approved or withdrawn. */
  perspectiveChange: PerspectiveChangeState | null;
}

export type PerspectiveChangeState =
  | { status: "pending"; request: PerspectiveChangeRequest }
  | { status: "refused"; request: PerspectiveChangeRequest; reason: string };

/**
 * The world as a player's screen shows it, from the deltas the game server sent this client. The
 * game master's own records, its skills and distribution lists, never reach a player.
 */
export interface WorldState {
  revision: number;
  /** Rounds are counted from 1; each completed jump opens the next. */
  round: number;
  /** The tick the current round began at, which its label dates. */
  roundTick: number;
  /** The jump under way: the round it ends, and what the players asked of it. */
  activeJump: { round: number; guidance?: string } | null;
  spectator: SpectatorState;
  perspectives: Record<string, PerspectiveState>;
  users: Record<string, UserState>;
  /** The perspectives on each distribution list, when the record is the complete one. */
  distributionLists: Record<string, string[]>;
  /** In a player's copy, what their perspective was forked with, until its `perspective.set`. */
  forkCopy?: ViewState;
}

/**
 * Reduces the full record, or a player's copy read as the player. Each view's timeline and map are
 * written once the whole record is read, from the writes the view saw.
 */
export function reduceWorld(
  history: WorldHistory | UserHistory,
  reader?: ProposingUser,
): WorldState {
  const state: WorldState = {
    revision: 0,
    round: 1,
    roundTick: 0,
    activeJump: null,
    spectator: {
      ...UNWRITTEN,
      scenes: [],
      aliases: {},
      perspectiveSuggestions: [],
    },
    perspectives: {},
    users: {},
    distributionLists: {},
  };
  for (const delta of history) applyDelta(state, delta, reader);
  for (const view of viewsOf(state)) {
    view.timeline = timelineWrittenBy(view.writes);
    view.map = mapAtEndOf(view.timeline);
  }
  return state;
}

/** The timeline of a view that has seen no writes. */
const EMPTY_TIMELINE = emptyTimeline();

/** A view before the record is read: no writes, and the timeline and map they write. */
const UNWRITTEN: Pick<ViewState, "writes" | "timeline" | "map"> = {
  writes: [],
  timeline: EMPTY_TIMELINE,
  map: [],
};

/** Every view the state holds: the spectator's, each perspective's, and a fork copy still gathering. */
function viewsOf(state: WorldState): ViewState[] {
  return [
    state.spectator,
    ...Object.values(state.perspectives),
    ...(state.forkCopy ? [state.forkCopy] : []),
  ];
}

/** A timeline lately written, and the writes that wrote it. */
interface WrittenTimeline {
  writes: readonly TimelineWrite[];
  timeline: Timeline;
}

/** Timelines lately written, the last used first, which reading a record again picks up from. */
const writtenLately: WrittenTimeline[] = [];
/** How many timelines are kept to pick up from: enough for every view of a few games. */
const WRITTEN_LATELY_KEPT = 32;

/**
 * The timeline a view's writes write. The same writes give the same timeline as they did lately,
 * and more writes are written onto the one their first ones wrote.
 */
function timelineWrittenBy(writes: readonly TimelineWrite[]): Timeline {
  if (writes.length === 0) return EMPTY_TIMELINE;
  const start = writtenLately
    .filter(
      written =>
        written.writes.length <= writes.length &&
        written.writes.every((write, index) => write === writes[index]),
    )
    .reduce<WrittenTimeline | undefined>(
      (longest, written) =>
        written.writes.length > (longest?.writes.length ?? 0) ? written : longest,
      undefined,
    );
  if (start) {
    writtenLately.splice(writtenLately.indexOf(start), 1);
    writtenLately.unshift(start);
    if (start.writes.length === writes.length) return start.timeline;
  }
  let timeline = start?.timeline ?? EMPTY_TIMELINE;
  for (const write of writes.slice(start?.writes.length ?? 0)) {
    timeline = writeTimeline(timeline, write);
  }
  writtenLately.unshift({ writes, timeline });
  writtenLately.splice(WRITTEN_LATELY_KEPT);
  return timeline;
}

/** The map where each timeline ends, worked out once per timeline. */
const mapsAtEnd = new WeakMap<Timeline, Thing[]>();

function mapAtEndOf(timeline: Timeline): Thing[] {
  let map = mapsAtEnd.get(timeline);
  if (!map) {
    map = mapAtEnd(timeline);
    mapsAtEnd.set(timeline, map);
  }
  return map;
}

/** How a view shows a perspective, by its handle: its name, icon, and colour there. */
export function aliasIn(view: ViewState, handle: string): Alias {
  return resolveAlias(view.aliases[handle]);
}

/** How a perspective shows itself. */
export function ownAlias(view: PerspectiveState): Alias {
  return aliasIn(view, view.perspective.handle);
}

/**
 * What a new perspective holds: nothing but a copy of the writes, scenes, and aliases of the view it
 * forks. In a player's copy they arrived as copied deltas, gathered in `forkCopy`.
 */
function startingView(
  state: WorldState,
  fork: Fork | "copy" | undefined,
): Omit<PerspectiveState, "perspective" | "userIds"> {
  const forked =
    fork === "copy"
      ? state.forkCopy
      : fork?.type === "spectator"
        ? state.spectator
        : Object.values(state.perspectives).find(view => view.perspective.handle === fork?.handle);
  const { scenes, aliases } = forked
    ? structuredClone({ scenes: forked.scenes, aliases: forked.aliases })
    : { scenes: [], aliases: {} };
  return {
    ...UNWRITTEN,
    writes: forked?.writes ?? [],
    scenes,
    aliases,
    jumpRequest: null,
    awaits: null,
    threads: [],
    pinnedThreadIds: [],
    actions: [],
    boardPlaces: {},
  };
}

/** The spectator and every perspective with users. */
function activeViews(state: WorldState): ViewRef[] {
  return [
    { type: "spectator" },
    ...Object.entries(state.perspectives)
      .filter(([, view]) => view.userIds.length > 0)
      .map(([id]) => ({ type: "perspective" as const, handle: id })),
  ];
}

/**
 * A perspective's view. In a player's copy, the deltas copied from the views their perspective was
 * forked from come before its `perspective.set`, so they gather in `forkCopy` until it arrives.
 */
function viewOf(state: WorldState, id: string, reader: ProposingUser | undefined): ViewState[] {
  const view = state.perspectives[id];
  if (view) return [view];
  if (id !== reader?.perspectiveHandle) return [];
  state.forkCopy ??= { ...UNWRITTEN, scenes: [], aliases: {} };
  return [state.forkCopy];
}

function applyDelta(
  state: WorldState,
  delta: CommittedDelta | UserDelta,
  reader: ProposingUser | undefined,
): void {
  state.revision = delta.revision;
  const { operation } = delta;
  const target = targetOf(delta, reader);
  const reached =
    target.type === "perspectives"
      ? reachedViews(target.perspectives, listId => state.distributionLists[listId])
      : target.type === "activeViews"
        ? activeViews(state)
        : [];
  const views: ViewState[] = reached.flatMap(ref =>
    ref.type === "spectator" ? [state.spectator as ViewState] : viewOf(state, ref.handle, reader),
  );
  /** A player's copy holds only their view's rounds; the full record's are the spectator's. */
  const reachesClock = reader !== undefined || reached.some(ref => ref.type === "spectator");
  const perspectives: PerspectiveState[] = reached.flatMap(ref =>
    ref.type === "perspective" ? (state.perspectives[ref.handle] ?? []) : [],
  );

  switch (operation.type) {
    case "perspective.set": {
      const { perspective, fork } = operation;
      state.perspectives[perspective.handle] = {
        ...startingView(state, perspective.handle === reader?.perspectiveHandle ? "copy" : fork),
        perspective,
        userIds: [],
      };
      delete state.forkCopy;
      return;
    }
    case "perspective.update":
      for (const view of perspectives) {
        view.perspective = applyChanges(view.perspective, operation.changes);
      }
      return;
    case "perspective.updateUsers":
      for (const view of perspectives) {
        view.userIds = [...operation.userIds];
        if (operation.userIds.length === 0) view.jumpRequest = null;
      }
      return;
    case "perspective.unset":
      for (const { perspective } of perspectives) {
        delete state.perspectives[perspective.handle];
        for (const [listId, handles] of Object.entries(state.distributionLists)) {
          state.distributionLists[listId] = handles.filter(handle => handle !== perspective.handle);
        }
      }
      return;
    case "distributionList.set":
      state.distributionLists[operation.distributionList.id] = [
        ...operation.distributionList.handles,
      ];
      return;
    case "distributionList.update":
      if (operation.changes.handles && state.distributionLists[operation.id]) {
        state.distributionLists[operation.id] = [...operation.changes.handles];
      }
      return;
    case "distributionList.perspectiveAdded": {
      const list = state.distributionLists[operation.id];
      if (list && !list.includes(operation.handle)) list.push(operation.handle);
      return;
    }
    case "distributionList.perspectiveRemoved": {
      const list = state.distributionLists[operation.id];
      if (list) {
        state.distributionLists[operation.id] = list.filter(handle => handle !== operation.handle);
      }
      return;
    }
    case "distributionList.unset":
      delete state.distributionLists[operation.id];
      return;
    case "perspectiveChange.requested":
    case "perspectiveChange.withdrawn":
    case "perspectiveChange.approved":
    case "perspectiveChange.refused": {
      if (target.type !== "users") return;
      for (const userId of target.userIds) {
        const user = userState(state, userId);
        const current = user.perspectiveChange;
        if (operation.type === "perspectiveChange.requested") {
          user.perspectiveChange = {
            status: "pending",
            request: { id: operation.id, userId, to: operation.to, revision: delta.revision },
          };
        } else if (current?.request.id !== operation.id) {
        } else if (operation.type === "perspectiveChange.refused") {
          user.perspectiveChange = {
            status: "refused",
            request: current.request,
            reason: operation.reason,
          };
        } else {
          user.perspectiveChange = null;
        }
      }
      return;
    }
    case "perspectiveSuggestion.set":
      state.spectator.perspectiveSuggestions.push(operation.perspectiveSuggestion);
      return;
    case "perspectiveSuggestion.update":
      state.spectator.perspectiveSuggestions = state.spectator.perspectiveSuggestions.map(
        suggestion =>
          suggestion.id === operation.id ? applyChanges(suggestion, operation.changes) : suggestion,
      );
      return;
    case "perspectiveSuggestion.unset":
      state.spectator.perspectiveSuggestions = state.spectator.perspectiveSuggestions.filter(
        suggestion => suggestion.id !== operation.id,
      );
      return;
    case "alias.set":
      for (const view of views) view.aliases[operation.handle] = operation.alias;
      return;
    case "alias.unset":
      for (const view of views) delete view.aliases[operation.handle];
      return;
    case "timeline.extended":
    case "timeline.clarified":
      for (const view of views) view.writes = [...view.writes, operation];
      return;
    case "scene.shown": {
      const { scene } = operation;
      for (const view of views) {
        view.scenes.push({
          id: scene.id,
          title: scene.title,
          story: sceneStory(scene),
          revision: delta.revision,
        });
      }
      return;
    }
    case "input.asked":
      for (const perspective of perspectives) perspective.awaits = operation.input;
      return;
    case "world.started":
      if (!reachesClock) return;
      state.round = operation.roundCompleted + 1;
      state.roundTick = operation.tick;
      return;
    case "input.answered":
    case "input.withdrawn":
      for (const perspective of perspectives) perspective.awaits = null;
      return;
    case "jump.intervened":
      return;
    case "jump.requested":
      for (const perspective of perspectives) {
        perspective.jumpRequest = operation.guidance ? { guidance: operation.guidance } : {};
      }
      return;
    case "jump.requestWithdrawn":
      for (const perspective of perspectives) perspective.jumpRequest = null;
      return;
    case "jump.started":
      for (const perspective of perspectives) {
        perspective.awaits = null;
        perspective.jumpRequest = null;
        for (const action of perspective.actions) {
          if (action.status !== "pending") continue;
          action.status = "committed";
          action.round = operation.round;
        }
      }
      if (!reachesClock) return;
      state.activeJump = {
        round: operation.round,
        ...(operation.guidance ? { guidance: operation.guidance } : {}),
      };
      return;
    case "jump.completed":
      for (const perspective of perspectives) perspective.awaits = null;
      if (!reachesClock) return;
      state.activeJump = null;
      state.round = operation.roundCompleted + 1;
      state.roundTick = operation.tick;
      return;
    case "thread.create":
      for (const perspective of perspectives) {
        perspective.threads.push({
          ...operation.thread,
          members: [...operation.thread.members],
          status: "open",
          entries: [],
          polls: {},
          reactions: {},
        });
      }
      return;
    case "thread.append":
      for (const perspective of perspectives) {
        const thread = perspective.threads.find(thread => thread.id === operation.threadId);
        if (!thread) continue;
        thread.entries.push(operation.entry);
        const { content, presentation } = operation.entry;
        if (content.type === "poll")
          thread.polls[content.pollId] = { status: "open", responses: [] };
        if (
          presentation.type === "perspective" &&
          perspective.awaits?.type === "reply" &&
          perspective.awaits.threadId === thread.id
        ) {
          perspective.awaits = null;
        }
      }
      return;
    case "thread.update":
      for (const perspective of perspectives) {
        const thread = perspective.threads.find(thread => thread.id === operation.id);
        if (!thread) continue;
        const { title, status, frame, members } = operation.changes;
        if (title !== undefined) thread.title = title;
        if (status !== undefined) thread.status = status;
        if (frame === null) delete thread.frame;
        else if (frame !== undefined) thread.frame = frame;
        for (const change of members ?? []) {
          if (change.type === "append") {
            thread.members = [
              ...thread.members.filter(member => member.id !== change.member.id),
              change.member,
            ];
          } else if (change.type === "update") {
            thread.members = thread.members.map(member =>
              member.id === change.id ? applyChanges(member, change.changes) : member,
            );
          } else {
            thread.members = thread.members.filter(member => member.id !== change.id);
          }
        }
      }
      return;
    case "thread.pollResponded":
      for (const perspective of perspectives) {
        const poll = perspective.threads.find(thread => thread.id === operation.threadId)?.polls[
          operation.pollId
        ];
        if (!poll) continue;
        const key = speakerKey(operation.respondent);
        poll.responses = poll.responses
          .filter(existing => speakerKey(existing.respondent) !== key)
          .concat({ respondent: operation.respondent, response: operation.response });
      }
      return;
    case "thread.reactionSet":
    case "thread.reactionUnset":
      for (const perspective of perspectives) {
        const thread = perspective.threads.find(thread => thread.id === operation.threadId);
        if (!thread) continue;
        const marks = thread.reactions[operation.entryId] ?? [];
        const key = speakerKey(operation.reactor);
        const others = (reaction: ThreadReaction) =>
          reaction.reactors.filter(reactor => speakerKey(reactor) !== key);
        const next =
          operation.type === "thread.reactionSet"
            ? marks.some(reaction => reaction.mark === operation.mark)
              ? marks.map(reaction =>
                  reaction.mark === operation.mark
                    ? { ...reaction, reactors: [...others(reaction), operation.reactor] }
                    : reaction,
                )
              : [...marks, { mark: operation.mark, reactors: [operation.reactor] }]
            : marks
                .map(reaction =>
                  reaction.mark === operation.mark
                    ? { ...reaction, reactors: others(reaction) }
                    : reaction,
                )
                .filter(reaction => reaction.reactors.length > 0);
        if (next.length > 0) thread.reactions[operation.entryId] = next;
        else delete thread.reactions[operation.entryId];
      }
      return;
    case "thread.pollClosed":
      for (const perspective of perspectives) {
        const poll = perspective.threads.find(thread => thread.id === operation.threadId)?.polls[
          operation.pollId
        ];
        if (poll) poll.status = "closed";
      }
      return;
    case "thread.pinsSet":
      for (const perspective of perspectives)
        perspective.pinnedThreadIds = [...operation.threadIds];
      return;
    case "action.submitted":
      for (const perspective of perspectives) {
        perspective.actions.push({ ...operation.action, status: "pending" });
      }
      return;
    case "action.edited":
    case "action.withdrawn":
      for (const perspective of perspectives) {
        const action = perspective.actions.find(action => action.id === operation.id);
        if (!action) continue;
        if (operation.type === "action.edited") action.text = operation.text;
        else action.status = "withdrawn";
      }
      return;
    case "board.arranged":
      for (const perspective of perspectives) {
        perspective.boardPlaces = {
          ...perspective.boardPlaces,
          [operation.canvasId]: operation.places,
        };
      }
      return;
    case "advisor.entryAdded":
      if (target.type !== "users") return;
      for (const userId of target.userIds) {
        const handle = handleOf(state, userId);
        if (!handle) continue;
        const { advisor } = userState(state, userId);
        advisor[handle] = [...(advisor[handle] ?? []), operation.entry];
      }
      return;
    case "advisor.cleared":
      if (target.type !== "users") return;
      for (const userId of target.userIds) {
        const handle = handleOf(state, userId);
        if (handle) delete userState(state, userId).advisor[handle];
      }
      return;
  }
}

/** The handle of the perspective a user is on, among those the state holds. */
function handleOf(state: WorldState, userId: string): string | undefined {
  return Object.values(state.perspectives).find(view => view.userIds.includes(userId))?.perspective
    .handle;
}

/** A user's advisor entries written on the perspective they are on now. */
export function advisorOf(state: WorldState, userId: string): AdvisorEntry[] {
  const handle = handleOf(state, userId);
  return handle ? (state.users[userId]?.advisor[handle] ?? []) : [];
}

function userState(state: WorldState, userId: string): UserState {
  const existing = state.users[userId];
  if (existing) return existing;
  const created: UserState = { advisor: {}, perspectiveChange: null };
  state.users[userId] = created;
  return created;
}

/**
 * Applies typed partial changes: omitted fields stay, `null` clears a field,
 * and `properties` merges key by key.
 */
function applyChanges<Value extends object>(value: Value, changes: object): Value {
  const next = { ...value } as Record<string, unknown>;
  for (const [key, change] of Object.entries(changes)) {
    if (change === undefined) continue;
    if (key === "public") {
      next.public = applyChanges((value as { public: object }).public, change as object);
    } else if (key === "properties") {
      const current = (value as { properties?: Properties }).properties;
      const properties = mergeProperties(current, change as PropertiesChanges);
      if (properties) next.properties = properties;
      else delete next.properties;
    } else if (change === null) {
      delete next[key];
    } else {
      next[key] = change;
    }
  }
  return next as Value;
}

function mergeProperties(
  properties: Properties | undefined,
  changes: PropertiesChanges,
): Properties | undefined {
  const merged: Properties = { ...properties };
  for (const [key, value] of Object.entries(changes)) {
    if (value === null) delete merged[key];
    else merged[key] = value;
  }
  return Object.keys(merged).length > 0 ? merged : undefined;
}
